<template>
  <v-container class="retouren-container">
    <!-- Formular für neue Retoure mit Glasmorphismus-Effekt -->
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">mdi-recycle</v-icon>
        Retouren Sorting
        <v-spacer></v-spacer>
        <v-chip color="success" size="small">Gerät registrieren</v-chip>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <v-card-text class="py-4">
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              ref="mengeField"
              v-model="menge"
              type="number"
              label="Anzahl"
              variant="outlined"
              min="1"
              @keyup.enter="openRetoureTypeDialog"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              class="form-field"
              color="success"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-counter</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="kommentar"
              label="Kommentar"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              auto-grow
              rows="3"
              class="form-field"
              color="success"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small"
                  >mdi-comment-text-outline</v-icon
                >
              </template>
            </v-textarea>
          </v-col>
        </v-row>

        <!-- Verbesserte Action-Schaltfläche mit deutlicherem inaktiven Zustand -->
        <v-btn
          block
          :color="menge > 0 ? 'success' : 'grey-darken-3'"
          class="mt-6 submit-btn"
          :class="{ 'disabled-btn': menge <= 0 }"
          size="large"
          elevation="2"
          @click="openRetoureTypeDialog"
          :disabled="menge <= 0"
        >
          <template v-if="menge > 0">
            <v-icon start>mdi-keyboard</v-icon>
            Fortfahren mit Geräteauswahl
          </template>
          <template v-else>
            <span class="keyboard-icon-wrapper">
              <v-icon start>mdi-keyboard</v-icon>
              <span class="strike-through"></span>
            </span>
            Anzahl eingeben
          </template>
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Darstellung gespeicherter Retouren -->
    <v-card class="status-overview-card">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2"
          >mdi-format-list-checks</v-icon
        >
        Gespeicherte Retouren Sortings
        <v-spacer></v-spacer>

        <div class="d-flex align-center">
          <v-tooltip location="bottom" text="Suchen">
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                class="mx-1"
                variant="text"
                v-bind="props"
                @click="retourenSearchDialog = true"
                color="success"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <!-- Verbesserte Tabelle mit modernem Design -->
      <v-data-table
        :headers="headers"
        :items="retouren"
        density="comfortable"
        class="data-table"
        :items-per-page="10"
        :items-per-page-options="[5, 10, 20, 50]"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <div class="date-cell" @click="showFullDate(item.erstelldatum)">
                {{ formatDate(item.erstelldatum) }}
                <v-tooltip activator="parent" location="top">
                  {{ formatDateTime(item.erstelldatum) }}
                </v-tooltip>
              </div>
            </td>

            <td>
              <v-chip
                size="small"
                class="typ-chip geraete-chip"
                color="success-lighten-5"
              >
                {{ getTypName(item.geraetetyp_id, "Gerätetyp") }}
              </v-chip>
            </td>

            <td>
              <v-chip
                size="small"
                class="typ-chip retouren-chip"
                color="success-lighten-5"
              >
                {{ getTypName(item.retourentyp_id, "Retourentyp") }}
              </v-chip>
            </td>

            <td>
              <div class="menge-cell">
                {{ item.menge }}
              </div>
            </td>

            <td>
              <div class="kommentar-cell">
                {{ truncateText(item.kommentar, 20) }}
                <v-tooltip
                  v-if="item.kommentar && item.kommentar.length > 20"
                  activator="parent"
                  location="top"
                  max-width="300"
                >
                  {{ item.kommentar }}
                </v-tooltip>
              </div>
            </td>

            <td class="action-buttons-cell">
              <div class="action-menu-wrapper">
                <v-menu
                  location="bottom"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset="5"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      size="small"
                      v-bind="props"
                      variant="text"
                      class="action-menu-btn"
                      color="grey-lighten-1"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list density="compact" class="action-menu-list">
                    <v-list-item
                      @click="editRetoure(item)"
                      prepend-icon="mdi-pencil"
                      title="Bearbeiten"
                      class="action-menu-item edit-action"
                      value="edit"
                    ></v-list-item>

                    <v-divider class="menu-divider"></v-divider>

                    <v-list-item
                      @click="confirmDelete(item.id)"
                      prepend-icon="mdi-delete"
                      title="Löschen"
                      class="action-menu-item delete-action"
                      value="delete"
                    ></v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </td>
          </tr>
        </template>

        <!-- Leerstand-Anzeige -->
        <template v-slot:no-data>
          <div class="no-data-message">
            <v-icon size="large" color="grey" class="mb-3"
              >mdi-database-off</v-icon
            >
            <p>Keine Retouren vorhanden</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog zur Auswahl von Gerätetyp und Retourentyp -->
    <v-dialog v-model="retoureTypeDialog" max-width="500" class="type-dialog">
      <v-card
        class="dialog-card"
        tabindex="0"
        @keydown.enter.stop.prevent="handleDialogEnter"
        @keydown.esc.stop.prevent="retoureTypeDialog = false"
        @keydown.tab="handleTabNavigation"
      >
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-devices</v-icon>
          Gerätetyp & Retourentyp wählen
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <div class="selection-container mb-4">
            <h4 class="selection-title d-flex align-center">
              <v-icon color="success" size="small" class="mr-2"
                >mdi-laptop</v-icon
              >
              Gerätetyp
            </h4>

            <div class="radio-options">
              <v-radio-group v-model="geraetetyp" mandatory inline>
                <v-radio
                  v-for="item in filteredGeraetetypen"
                  :key="item.id"
                  :value="item.id"
                  :label="item.name"
                  color="success"
                  class="type-radio"
                ></v-radio>
              </v-radio-group>
            </div>
          </div>

          <v-divider class="selection-divider my-4"></v-divider>

          <div class="selection-container">
            <h4 class="selection-title d-flex align-center">
              <v-icon color="success" size="small" class="mr-2"
                >mdi-package-variant-closed-remove</v-icon
              >
              Retourentyp
            </h4>

            <div class="radio-options">
              <v-radio-group v-model="retourentyp" mandatory inline>
                <v-radio
                  v-for="item in filteredRetourentypen"
                  :key="item.id"
                  :value="item.id"
                  :label="item.name"
                  color="success"
                  class="type-radio"
                ></v-radio>
              </v-radio-group>
            </div>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="retoureTypeDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :color="geraetetyp && retourentyp ? 'success' : 'grey-darken-1'"
            variant="elevated"
            :disabled="!geraetetyp || !retourentyp"
            @click="confirmRetoureType"
          >
            <v-icon start>mdi-check</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bearbeiten-Dialog -->
    <v-dialog v-model="editDialog" max-width="550" class="edit-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-pencil-box</v-icon>
          Retoure bearbeiten
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <v-row>
            <v-col cols="12">
              <div class="selection-container mb-3">
                <h4 class="selection-title d-flex align-center">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-laptop</v-icon
                  >
                  Gerätetyp
                </h4>
                <v-radio-group
                  v-model="editItem.geraetetyp_id"
                  mandatory
                  inline
                >
                  <v-radio
                    v-for="item in filteredGeraetetypen"
                    :key="item.id"
                    :value="item.id"
                    :label="item.name"
                    color="success"
                    class="type-radio"
                  ></v-radio>
                </v-radio-group>
              </div>
            </v-col>

            <v-col cols="12">
              <div class="selection-container mb-3">
                <h4 class="selection-title d-flex align-center">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-package-variant-closed-remove</v-icon
                  >
                  Retourentyp
                </h4>
                <v-radio-group
                  v-model="editItem.retourentyp_id"
                  mandatory
                  inline
                >
                  <v-radio
                    v-for="item in filteredRetourentypen"
                    :key="item.id"
                    :value="item.id"
                    :label="item.name"
                    color="success"
                    class="type-radio"
                  ></v-radio>
                </v-radio-group>
              </div>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editItem.menge"
                type="number"
                label="Anzahl"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-counter</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="editItem.kommentar"
                label="Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                auto-grow
                rows="2"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-comment-text-outline</v-icon
                  >
                </template>
              </v-textarea>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="editItem.erstelldatum"
                label="Datum und Uhrzeit"
                type="datetime-local"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-calendar-clock</v-icon
                  >
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="editDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" variant="elevated" @click="updateRetoure">
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Löschen-Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" class="delete-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title warning-header d-flex align-center">
          <v-icon color="error" size="24" class="mr-2">mdi-alert-circle</v-icon>
          Löschen bestätigen
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content text-center pa-6">
          <p>Bist du sicher, dass du diesen Eintrag löschen möchtest?</p>
          <p class="text-caption mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="deleteDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="elevated" @click="deleteRetoure">
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Verbesserter Suchdialog -->
    <v-dialog
      v-model="retourenSearchDialog"
      max-width="800"
      class="search-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-magnify</v-icon>
          Retouren durchsuchen
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="retourenSearchDialog = false"
          ></v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <v-text-field
            v-model="retourenSearchQuery"
            label="Suchbegriff eingeben..."
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            clearable
            hide-details
            class="search-field mb-4"
            prepend-inner-icon="mdi-magnify"
            @keyup.esc="retourenSearchDialog = false"
            color="success"
          ></v-text-field>

          <!-- Suchergebnisse -->
          <div v-if="retourenSearchQuery" class="search-results">
            <v-data-table
              :headers="headers"
              :items="filteredRetouren"
              density="comfortable"
              class="data-table search-table"
              hide-default-footer
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>
                    <div
                      class="date-cell"
                      @click="showFullDate(item.erstelldatum)"
                    >
                      {{ formatDate(item.erstelldatum) }}
                      <v-tooltip activator="parent" location="top">
                        {{ formatDateTime(item.erstelldatum) }}
                      </v-tooltip>
                    </div>
                  </td>

                  <td>
                    <v-chip
                      size="small"
                      class="typ-chip geraete-chip"
                      color="success-lighten-5"
                    >
                      {{ getTypName(item.geraetetyp_id, "Gerätetyp") }}
                    </v-chip>
                  </td>

                  <td>
                    <v-chip
                      size="small"
                      class="typ-chip retouren-chip"
                      color="success-lighten-5"
                    >
                      {{ getTypName(item.retourentyp_id, "Retourentyp") }}
                    </v-chip>
                  </td>

                  <td>
                    <div class="menge-cell">
                      {{ item.menge }}
                    </div>
                  </td>

                  <td>
                    <div class="kommentar-cell">
                      {{ truncateText(item.kommentar, 20) }}
                      <v-tooltip
                        v-if="item.kommentar && item.kommentar.length > 20"
                        activator="parent"
                        location="top"
                        max-width="300"
                      >
                        {{ item.kommentar }}
                      </v-tooltip>
                    </div>
                  </td>

                  <td class="action-buttons-cell">
                    <div class="action-menu-wrapper">
                      <v-menu
                        location="bottom"
                        :close-on-content-click="true"
                        offset="5"
                      >
                      </v-menu>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Keine Suchergebnisse -->
            <div v-if="filteredRetouren.length === 0" class="no-results">
              <v-icon size="large" color="grey" class="mb-2"
                >mdi-file-search-outline</v-icon
              >
              <p>Keine passenden Einträge gefunden</p>
            </div>
          </div>

          <!-- Initiale Ansicht (ohne Suchbegriff) -->
          <div v-else class="search-placeholder">
            <v-icon size="x-large" color="grey" class="mb-4"
              >mdi-magnify</v-icon
            >
            <p>Gib einen Suchbegriff ein, um Retouren zu finden</p>
            <p class="text-caption mt-2">
              Du kannst nach Kommentaren oder Gerätetypen suchen
            </p>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="retourenSearchDialog = false"
          >
            <v-icon start>mdi-close</v-icon>
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();

// Neue Referenzen für DOM-Elemente
const mengeField = ref(null);

// ======== Zustandsverwaltung ========
// Form-Zustände (ohne Auftragsnummer)
const menge = ref(1);
const kommentar = ref("");
const geraetetyp = ref(null);
const retourentyp = ref(null);
const formSubmitting = ref(false);

// Listen-Zustände
const retouren = ref([]);
const geraetetypen = ref([]);
const retourentypen = ref([]);
const loading = ref({
  initial: true,
  fetching: false,
  submitting: false,
  updating: false,
  deleting: false,
});

// UI-Zustände
const actionsVisible = ref(false);

// Dialog-Zustände
const retoureTypeDialog = ref(false);
const editDialog = ref(false);
const deleteDialog = ref(false);
const retourenSearchDialog = ref(false);

// Such- und Bearbeiten-Zustände
const retourenSearchQuery = ref("");
const deleteId = ref(null);
const editItem = ref({});

// ======== Computed Properties ========
// Tabellenheader ohne Auftragsnummer
const headers = computed(() => [
  { title: "Datum", key: "erstelldatum", align: "start", sortable: true },
  { title: "Gerätetyp", key: "geraetetyp_id", align: "start", sortable: true },
  {
    title: "Retourentyp",
    key: "retourentyp_id",
    align: "start",
    sortable: true,
  },
  { title: "Anzahl", key: "menge", align: "center", sortable: true },
  { title: "Kommentar", key: "kommentar", align: "start", sortable: false },
  { title: "Aktionen", key: "actions", align: "center", sortable: false },
]);

// Neue Funktion für Enter-Taste im Dialog
const handleDialogEnter = (e) => {
  e.stopPropagation();
  e.preventDefault();

  if (geraetetyp.value && retourentyp.value) {
    confirmRetoureType();
  }
};

// Gefilterte Retouren für die Suchfunktion (ohne Auftragsnummer)
const filteredRetouren = computed(() => {
  if (!retourenSearchQuery.value) return [];

  const query = retourenSearchQuery.value.toLowerCase().trim();

  return retouren.value.filter((item) => {
    // Suche im Kommentar
    if (item.kommentar && item.kommentar.toLowerCase().includes(query)) {
      return true;
    }

    // Suche nach Gerätetyp
    const geraeteTypName = getTypName(
      item.geraetetyp_id,
      "Gerätetyp"
    ).toLowerCase();
    if (geraeteTypName.includes(query)) {
      return true;
    }

    // Suche nach Retourentyp
    const retourenTypName = getTypName(
      item.retourentyp_id,
      "Retourentyp"
    ).toLowerCase();
    if (retourenTypName.includes(query)) {
      return true;
    }

    // Suche nach Datum
    const itemDate = formatDateTime(item.erstelldatum).toLowerCase();
    if (itemDate.includes(query)) {
      return true;
    }

    return false;
  });
});

// FormValidation-Zustand (vereinfacht ohne Auftragsnummer)
const formValid = computed(() => {
  return menge.value > 0;
});

// ======== UI Funktionen ========
// Text-Hilfsfunktionen
const truncateText = (text, length) => {
  if (!text) return "—";
  if (text.length <= length) return text;
  return text.substring(0, length) + "...";
};

// ======== Datum-Funktionen ========
const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Ungültiges Datum";

    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    console.error("Datum-Formatierungsfehler:", e);
    return "Fehler beim Formatieren";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Ungültiges Datum";

    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    console.error("Datum-Formatierungsfehler:", e);
    return "Fehler beim Formatieren";
  }
};

const showFullDate = (dateString) => {
  toast.info("Datum/Uhrzeit: " + formatDateTime(dateString));
};

// ======== API-Interaktionen ========
const fetchRetouren = async () => {
  if (!authStore.user?.id) return;

  loading.value.fetching = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      { action: "get", typ: "Retoure" },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      // Filtere nur Retouren mit Gerätetyp "RR" und Retourentyp "Retouren Sorting"
      const rrGeraetetyp = geraetetypen.value.find(typ => typ.name === "RR");
      const retourenSortingTyp = retourentypen.value.find(typ => typ.name === "Retouren Sorting");

      let filteredRetouren = response.data.auftraege;

      if (rrGeraetetyp && retourenSortingTyp) {
        filteredRetouren = response.data.auftraege.filter(retoure =>
          retoure.geraetetyp_id === rrGeraetetyp.id &&
          retoure.retourentyp_id === retourenSortingTyp.id
        );
      } else {
        // Fallback: Zeige keine Retouren an, wenn Typen nicht gefunden werden
        filteredRetouren = [];
      }

      retouren.value = filteredRetouren.sort(
        (a, b) => new Date(b.erstelldatum) - new Date(a.erstelldatum)
      );
    } else {
      toast.error("Fehler beim Laden der Retouren: " + response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Laden der Retouren:", error);
    toast.error("Verbindungsfehler beim Laden der Retouren.");
  } finally {
    loading.value.fetching = false;
    loading.value.initial = false;
  }
};

const handleTabNavigation = (e) => {
  const focusableElements = document
    .querySelector(".type-dialog")
    .querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (e.shiftKey && document.activeElement === firstElement) {
    e.preventDefault();
    lastElement.focus();
  } else if (!e.shiftKey && document.activeElement === lastElement) {
    e.preventDefault();
    firstElement.focus();
  }
};

const fetchTypen = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      geraetetypen.value = response.data.geraetetypen || [];
      retourentypen.value = response.data.retourentypen || [];
    } else {
      toast.error("Fehler beim Laden der Kategorien: " + response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Laden der Typen:", error);
    toast.error("Verbindungsfehler beim Laden der Kategoriedaten.");
  }
};

const getTypName = (id, typ) => {
  const typListe =
    typ === "Gerätetyp" ? geraetetypen.value : retourentypen.value;
  const found = typListe.find((item) => item.id === id);
  return found ? found.name : "Unbekannt";
};

const typeDialogJustOpened = ref(false);

// ======== Formular-Funktionen ========
const openRetoureTypeDialog = async () => {
  if (typeDialogJustOpened.value) {
    return;
  }
  if (menge.value <= 0) {
    toast.error("Die Anzahl muss größer als 0 sein!");
    return;
  }

  // Finde "RR" Gerätetyp und "Retouren Sorting" Retourentyp automatisch
  const rrGeraetetyp = geraetetypen.value.find(
    (typ) => typ.name === "RR"
  );
  const retourenSortingTyp = retourentypen.value.find(
    (typ) => typ.name === "Retouren Sorting"
  );

  if (rrGeraetetyp) {
    geraetetyp.value = rrGeraetetyp.id;
  }
  if (retourenSortingTyp) {
    retourentyp.value = retourenSortingTyp.id;
  }

  retoureTypeDialog.value = true;
  typeDialogJustOpened.value = true;

  setTimeout(() => {
    typeDialogJustOpened.value = false;
  }, 500);

  nextTick(() => {
    const dialogElement = document.querySelector(".type-dialog .dialog-card");
    if (dialogElement) {
      dialogElement.focus();
    }
  });
};

const confirmRetoureType = async () => {
  if (!geraetetyp.value || !retourentyp.value) {
    toast.error(
      "Bitte wähle sowohl einen Gerätetyp als auch einen Retourentyp aus!"
    );
    return;
  }

  retoureTypeDialog.value = false;
  await submitRetoure();
  typeDialogJustOpened.value = true;
  setTimeout(() => {
    typeDialogJustOpened.value = false;
  }, 500);
};

const submitRetoure = async () => {
  try {
    if (!authStore.user?.id) {
      toast.error("Bitte melde dich an, um Retouren zu erfassen.");
      return;
    }

    if (!formValid.value) {
      toast.error("Bitte alle Felder korrekt ausfüllen!");
      return;
    }

    formSubmitting.value = true;

    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "add",
        typ: "Retoure",
        geraetetyp_id: geraetetyp.value,
        retourentyp_id: retourentyp.value,
        auftragsnummer: "", // Leer für Retouren Sorting
        menge: menge.value,
        kommentar: kommentar.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      await fetchRetouren();
      toast.success("Retoure erfolgreich gespeichert!");
      resetForm();
    } else {
      toast.error(response.data.message || "Fehler beim Speichern!");
    }
  } catch (error) {
    console.error("Fehler beim Speichern der Retoure:", error);
    toast.error("Verbindungsfehler beim Speichern der Retoure.");
  } finally {
    formSubmitting.value = false;
  }
};

const resetForm = () => {
  menge.value = 1;
  kommentar.value = "";
  geraetetyp.value = null;
  retourentyp.value = null;

  nextTick(() => {
    if (mengeField.value) {
      mengeField.value.focus();
    }
  });
};

// ======== Bearbeiten-Funktionen ========
const editRetoure = (item) => {
  editItem.value = { ...item };

  try {
    const date = new Date(item.erstelldatum);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      editItem.value.erstelldatum = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
  } catch (e) {
    console.error("Fehler bei der Datumskonvertierung:", e);
  }

  editDialog.value = true;
};

const updateRetoure = async () => {
  try {
    loading.value.updating = true;

    let formattedDate;
    if (editItem.value.erstelldatum) {
      const date = new Date(editItem.value.erstelldatum);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } else {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");

      formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "update",
        id: editItem.value.id,
        typ: "Retoure",
        geraetetyp_id: editItem.value.geraetetyp_id,
        retourentyp_id: editItem.value.retourentyp_id,
        auftragsnummer: "", // Leer für Retouren Sorting
        menge: editItem.value.menge,
        kommentar: editItem.value.kommentar,
        erstelldatum: formattedDate,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      await fetchRetouren();
      toast.success("Retoure erfolgreich aktualisiert!");
      editDialog.value = false;
    } else {
      toast.error(response.data.message || "Fehler beim Aktualisieren!");
    }
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Retoure:", error);
    toast.error("Verbindungsfehler beim Aktualisieren.");
  } finally {
    loading.value.updating = false;
  }
};

// ======== Löschen-Funktionen ========
const confirmDelete = (id) => {
  deleteId.value = id;
  deleteDialog.value = true;
};

const deleteRetoure = async () => {
  try {
    loading.value.deleting = true;

    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "delete",
        id: deleteId.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      await fetchRetouren();
      toast.success("Retoure erfolgreich gelöscht!");
      deleteDialog.value = false;
    } else {
      toast.error(response.data.message || "Fehler beim Löschen!");
    }
  } catch (error) {
    console.error("Fehler beim Löschen der Retoure:", error);
    toast.error("Verbindungsfehler beim Löschen.");
  } finally {
    loading.value.deleting = false;
  }
};

// ======== Lebenszyklus-Hooks ========
onMounted(async () => {
  // Erst die Typen laden, dann die Retouren
  await fetchTypen();
  if (authStore.user) {
    await fetchRetouren();
  }

  nextTick(() => {
    if (mengeField.value) {
      mengeField.value.focus();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (retoureTypeDialog.value) retoureTypeDialog.value = false;
      else if (editDialog.value) editDialog.value = false;
      else if (deleteDialog.value) deleteDialog.value = false;
      else if (retourenSearchDialog.value) retourenSearchDialog.value = false;
    }
  });
});

// Watcher für User-Änderungen - auch hier fetchTypen zuerst
watch(
  () => authStore.user,
  async (newUser, oldUser) => {
    if (newUser && !oldUser) {
      await fetchTypen();
      await fetchRetouren();
    } else if (!newUser && oldUser) {
      retouren.value = [];
    }
  }
);

// Computed Properties für gefilterte Typen - nur "RR" und "Retouren Sorting"
const filteredGeraetetypen = computed(() => {
  return geraetetypen.value.filter(typ => typ.name === "RR");
});

const filteredRetourentypen = computed(() => {
  return retourentypen.value.filter(typ => typ.name === "Retouren Sorting");
});
</script>

<style scoped>
/* ======== Container & Basis-Layout ======== */
.retouren-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

/* ======== Karten & Container ======== */
.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.card-divider {
  opacity: 0.1;
}

.dialog-card {
  background-color: rgba(30, 30, 35, 0.9);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.dialog-title {
  padding: 16px;
  font-size: a1.1rem;
  font-weight: 500;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dialog-divider {
  opacity: 0.1;
}

.dialog-content {
  padding: 20px;
}

.dialog-actions {
  padding: 12px 16px;
}

.warning-header {
  background: rgba(var(--v-theme-error), 0.1);
}

/* ======== Eingabefelder & Formulare ======== */
.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.submit-btn {
  height: 50px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-btn[disabled] {
  opacity: 0.65 !important;
  box-shadow: none !important;
  background-color: rgba(60, 60, 60, 0.8) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.submit-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.6s;
}

.submit-btn:hover:not([disabled]) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.submit-btn:hover:not([disabled])::before {
  left: 100%;
}

.search-field :deep(.v-field__prepend-inner) {
  opacity: 0.8;
}

.disabled-btn {
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  background-color: rgba(60, 60, 60, 0.8) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  opacity: 0.9 !important;
}

.disabled-btn:hover {
  transform: none !important;
  box-shadow: none !important;
}

.disabled-btn::before {
  display: none !important;
}

.keyboard-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
}

.strike-through {
  position: absolute;
  display: block;
  width: 130%;
  height: 2px;
  background-color: rgba(255, 80, 80, 0.7);
  transform: rotate(-45deg);
  left: -15%;
  top: 50%;
}

/* ======== Typ-Auswahl im Dialog ======== */
.selection-container {
  margin-bottom: 16px;
}

.selection-title {
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.selection-divider {
  opacity: 0.1;
}

.radio-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.type-radio :deep(.v-selection-control__wrapper) {
  opacity: 0.9;
}

.type-radio :deep(.v-label) {
  opacity: 0.85;
}

/* ======== Tabellen-Design ======== */
.data-table {
  background: transparent !important;
}

.data-table :deep(table) {
  background: transparent !important;
  border-spacing: 0;
}

.data-table :deep(tr:hover td) {
  background: rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(th) {
  background: rgba(20, 20, 25, 0.5) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(td) {
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.85);
}

.data-table :deep(.v-data-table-footer) {
  background: transparent !important;
}

.data-table :deep(.v-data-table-footer__items-per-page) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-select .v-field) {
  background: rgba(30, 30, 40, 0.5) !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

.data-table :deep(.v-data-table-footer .v-btn) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-btn--disabled) {
  color: rgba(255, 255, 255, 0.3) !important;
}

.date-cell {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.date-cell:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.typ-chip {
  font-size: 0.75rem !important;
  font-weight: 500 !important;
}

.geraete-chip {
  background: rgba(76, 175, 80, 0.15) !important;
  color: rgba(76, 175, 80, 0.9) !important;
}

.retouren-chip {
  background: rgba(76, 175, 80, 0.15) !important;
  color: rgba(76, 175, 80, 0.9) !important;
}

.kommentar-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.menge-cell {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* ======== Aktionsbuttons in Tabellen ======== */
.action-buttons-cell {
  width: 48px !important;
  text-align: center;
}

.action-menu-wrapper {
  display: flex;
  justify-content: center;
}

.action-menu-btn {
  opacity: 0.7;
  transition: all 0.2s ease;
}

.action-menu-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.action-menu-list {
  background-color: rgba(35, 35, 40, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
  min-width: 180px;
}

.action-menu-item {
  transition: background-color 0.2s ease;
}

.edit-action:hover {
  background-color: rgba(76, 175, 80, 0.1) !important;
}

.edit-action:hover :deep(.v-list-item-title),
.edit-action:hover :deep(.v-icon) {
  color: rgba(76, 175, 80, 0.9) !important;
}

.delete-action:hover {
  background-color: rgba(244, 67, 54, 0.1) !important;
}

.delete-action:hover :deep(.v-list-item-title),
.delete-action:hover :deep(.v-icon) {
  color: rgba(244, 67, 54, 0.9) !important;
}

.menu-divider {
  opacity: 0.1;
}

/* ======== Leerstand & Platzhaltermeldungen ======== */
.no-data-message,
.no-results,
.search-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.search-placeholder {
  padding: 60px 20px;
}

.search-results {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 960px) {
  .radio-options {
    flex-direction: column;
    gap: 0;
  }

  .action-buttons-cell {
    width: 48px;
  }
}

@media (max-width: 600px) {
  .dialog-content {
    padding: 16px;
  }

  .dialog-title {
    font-size: 1rem;
    padding: 12px 16px;
  }

  .selection-title {
    font-size: 0.9rem;
  }

  .data-table :deep(th),
  .data-table :deep(td) {
    padding: 8px 12px !important;
  }

  .typ-chip {
    font-size: 0.7rem !important;
  }

  .search-placeholder {
    padding: 40px 16px;
  }
}
</style>
